import type { CurrentUser } from '@capsio/contracts';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, messageOf } from '../api-client.js';
import { AtmospherePanel } from '../components/AtmospherePanel.js';
import { CodeInput, useResendCountdown } from '../components/CodeInput.js';
import { BusyButton, Field, PasswordMeter } from '../components/Field.js';

type Step = 'email' | 'code' | 'password' | 'no-mail';

export function ForgotPasswordPage({
  mailConfigured,
  onSignedIn,
}: {
  mailConfigured: boolean;
  onSignedIn: (user: CurrentUser) => void;
}): ReactNode {
  // 没配发信就没有验证码可发，直接进「找管理员」那一屏，别让人在输入框前干等。
  const [step, setStep] = useState<Step>(mailConfigured ? 'email' : 'no-mail');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const resend = useResendCountdown();

  async function sendCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.requestPasswordReset(email.trim());
      if (!result.delivered) {
        setStep('no-mail');
        return;
      }
      setMaskedEmail(result.maskedEmail);
      setCode('');
      setStep('code');
      resend.start();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function complete(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password.length < 12) {
      setError('密码至少 12 位');
      return;
    }
    if (confirm !== password) {
      setError('两次输入不一致');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.completePasswordReset(email.trim(), code, password));
    } catch (cause) {
      setError(messageOf(cause));
      // 码错了要退回上一步重填，留在这一屏改密码是改不出结果的。
      if (messageOf(cause).includes('验证码')) setStep('code');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="split">
      <AtmospherePanel
        depth={4}
        kicker="找回访问权"
        statement={step === 'no-mail' ? '这条路暂时不通' : '邮箱是唯一的钥匙'}
        sub={
          step === 'no-mail'
            ? '找回密码要靠邮件，而这套系统还没有发信通道。'
            : '账号既然由管理员添加，找回也只认那个登记过的邮箱。'
        }
      />

      <main className="form-side">
        <div className="col">
          {step === 'no-mail' && (
            <>
              <div className="head">
                <h1>这套系统还发不出邮件</h1>
                <p>没有发信通道，验证码就发不出来，密码也就找不回来。</p>
              </div>
              <div className="call brass">
                <span className="ic">!</span>
                <p>
                  管理员也代替不了：重置密码同样要靠发邮件。让他先去「设置 → 发信邮箱」配好，
                  之后你回到这一页就能自助重置了。
                </p>
              </div>
              <Link className="btn quiet" to="/login" style={{ textDecoration: 'none' }}>
                返回登录
              </Link>
            </>
          )}

          {step === 'email' && (
            <>
              <div className="head">
                <h1>重置密码</h1>
                <p>填你的登录邮箱，我们发一个 6 位验证码过去。</p>
              </div>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void sendCode();
                }}
              >
                <Field id="forgot-email" label="登录邮箱" error={error}>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="username"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
                <div className="row-actions">
                  <Link
                    className="btn quiet"
                    to="/login"
                    style={{ textDecoration: 'none', flex: '0 0 auto' }}
                  >
                    返回登录
                  </Link>
                  <BusyButton busy={busy} busyLabel="发送中" type="submit">
                    发送验证码
                  </BusyButton>
                </div>
              </form>
            </>
          )}

          {step === 'code' && (
            <>
              <div className="head">
                <h1>输入验证码</h1>
                <p>
                  已发往 <strong>{maskedEmail ?? email}</strong>，10 分钟内有效。
                </p>
              </div>
              <Field id="forgot-code" label="验证码" error={error}>
                <CodeInput value={code} onChange={setCode} invalid={error !== null} autoFocus />
              </Field>
              <div className="resend">
                <span>没收到？</span>
                <button
                  className="linkbtn"
                  type="button"
                  disabled={resend.seconds > 0 || busy}
                  onClick={() => void sendCode()}
                >
                  {resend.seconds > 0 ? `重新发送 (${resend.seconds})` : '重新发送'}
                </button>
              </div>
              <button
                className="btn"
                type="button"
                disabled={code.length < 6}
                onClick={() => {
                  setError(null);
                  setStep('password');
                }}
              >
                下一步
              </button>
            </>
          )}

          {step === 'password' && (
            <>
              <div className="head">
                <h1>设置新密码</h1>
                <p>设置成功后，其他设备上的登录会全部失效。</p>
              </div>
              <form onSubmit={(event) => void complete(event)}>
                <Field
                  id="new-pw"
                  label="新密码"
                  aux="至少 12 位"
                  hint={<PasswordMeter value={password} />}
                >
                  <input
                    id="new-pw"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>
                <Field id="new-pw2" label="再输一次" error={error}>
                  <input
                    id="new-pw2"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••••••"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                  />
                </Field>
                <BusyButton busy={busy} busyLabel="重置中" type="submit">
                  重置并登录
                </BusyButton>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
